import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Github, RotateCw } from "lucide-react";
import { Card, PageHeader } from "../components/ui.jsx";
import { useAuth } from "../auth.jsx";

const messages = {
  state: {
    title: "This GitHub setup link is no longer valid",
    body: "The one-time verification link was missing, already used, or belonged to a different signed-in DebutDeploy account. GitHub has not been connected yet.",
  },
  expired: {
    title: "The GitHub setup link expired",
    body: "For security, setup links expire after 15 minutes. Start a fresh connection from DebutDeploy.",
  },
  identity: {
    title: "Connect the matching GitHub identity first",
    body: "DebutDeploy checks that the GitHub App installation belongs to the GitHub identity linked to your panel account. Sign in with the same verified email you use for this DebutDeploy account, then start the connection again.",
  },
  account: {
    title: "The GitHub account did not match",
    body: "The installation belongs to a different GitHub account than the one linked to this DebutDeploy account. Sign in with the matching GitHub identity and retry. Personal GitHub accounts are supported for this connection.",
  },
  installation: {
    title: "GitHub did not return an installation",
    body: "The GitHub App installation was not included in the callback. Start again from DebutDeploy and finish the installation flow there.",
  },
};

export default function GitHubSetupHelp() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const message = messages[params.get("reason")] || messages.state;

  return (
    <div className="page mx-auto max-w-3xl space-y-5">
      <PageHeader title="Connect GitHub" subtitle="Link the right GitHub account before creating an application." />
      <Card>
        <div className="flex items-start gap-3">
          <AlertTriangle size={21} className="mt-0.5 shrink-0" style={{ color: "var(--err)" }} />
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{message.title}</h2>
            <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>{message.body}</p>
          </div>
        </div>
        <ol className="mt-5 list-decimal space-y-2 pl-5 text-sm" style={{ color: "var(--text-muted)" }}>
          <li>Use the GitHub account that owns the repositories you want to deploy.</li>
          <li>Return to <Link className="underline" to="/new">New Service</Link> and choose <strong style={{ color: "var(--text)" }}>Connect GitHub</strong>.</li>
          <li>Install or authorize the DebutDeploy GitHub App, then let GitHub return to DebutDeploy. Do not reopen an old callback or use GitHub's Configure/Update page directly.</li>
          <li>When setup succeeds, choose a repository and branch in the application form. DebutDeploy checks the installation account before listing repositories.</li>
        </ol>
        <div className="mt-6 flex flex-wrap gap-2">
          {!user && <a className="btn btn-secondary inline-flex items-center gap-2" href="/auth/github?returnTo=%2Fnew"><Github size={15} /> Sign in with GitHub</a>}
          <Link className="btn btn-primary inline-flex items-center gap-2" to="/new"><RotateCw size={15} /> Start again from New Service</Link>
          <Link className="btn btn-secondary inline-flex items-center gap-2" to="/settings">Account settings <ArrowRight size={15} /></Link>
        </div>
      </Card>
    </div>
  );
}
